import React, { useState, useEffect, useCallback } from 'react';
import {
  Add as PlusIcon,
  Edit as PencilIcon,
  Delete as TrashIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  Refresh as ArrowPathIcon
} from '@mui/icons-material';
import {
  getPlaybookLibraryItems,
  createPlaybookLibraryItem,
  updatePlaybookLibraryItem,
  deletePlaybookLibraryItem,
  uploadPlaybookLibraryExcel,
  downloadPlaybookLibraryTemplate,
  type PlaybookLibraryItem,
  type PlaybookLibraryType
} from '../services/apiService';

interface PlaybookLibraryManagementProps {
  setError: (error: string | null) => void;
  setSuccess: (success: string | null) => void;
}

interface SectionConfig {
  type: PlaybookLibraryType;
  label: string;
}

const SECTIONS: SectionConfig[] = [
  { type: 'pitch', label: 'Intro Pitch' },
  { type: 'question', label: 'Qualifying Questions' },
  { type: 'advanced', label: 'Deal Progression' }
];

const PlaybookLibraryManagement: React.FC<PlaybookLibraryManagementProps> = ({
  setError,
  setSuccess
}) => {
  const [items, setItems] = useState<PlaybookLibraryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<PlaybookLibraryType>>(
    new Set(['pitch', 'question', 'advanced'])
  );

  // Inline add state per section
  const [addingText, setAddingText] = useState<Record<PlaybookLibraryType, string>>({
    pitch: '',
    question: '',
    advanced: ''
  });
  const [addingInSection, setAddingInSection] = useState<PlaybookLibraryType | null>(null);

  // Inline edit state
  const [deleting, setDeleting] = useState(false);

  // Inline edit state
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getPlaybookLibraryItems();
      setItems(response?.items || []);
    } catch (error: any) {
      console.error('Error fetching library items:', error);
      setError('Failed to load library items');
    } finally {
      setLoading(false);
    }
  }, [setError]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const getItemsByType = (type: PlaybookLibraryType): PlaybookLibraryItem[] => {
    return items.filter(item => item.type === type);
  };

  const toggleSection = (type: PlaybookLibraryType) => {
    const updated = new Set(expandedSections);
    if (updated.has(type)) {
      updated.delete(type);
    } else {
      updated.add(type);
    }
    setExpandedSections(updated);
  };

  const handleAddItem = async (type: PlaybookLibraryType) => {
    const text = addingText[type]?.trim();
    if (!text) return;

    try {
      await createPlaybookLibraryItem(type, text);
      setAddingText(prev => ({ ...prev, [type]: '' }));
      setAddingInSection(null);
      setSuccess('Item added');
      await fetchItems();
    } catch (error: any) {
      setError(error?.message || 'Failed to add item');
    }
  };

  const handleUpdateItem = async (id: string) => {
    const text = editingText.trim();
    if (!text) return;

    try {
      await updatePlaybookLibraryItem(id, text);
      setEditingItemId(null);
      setEditingText('');
      setSuccess('Item updated');
      await fetchItems();
    } catch (error: any) {
      setError(error?.message || 'Failed to update item');
    }
  };

  const handleDeleteItem = async (id: string) => {
    if (!window.confirm('Delete this library item?')) return;

    setDeleting(true);
    try {
      await deletePlaybookLibraryItem(id);
      setSuccess('Item deleted');
      await fetchItems();
    } catch (error: any) {
      setError(error?.message || 'Failed to delete item');
    } finally {
      setDeleting(false);
    }
  };

  const handleUploadExcel = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await uploadPlaybookLibraryExcel(file);
      setSuccess(result?.message || 'Upload complete');
      await fetchItems();
    } catch (error: any) {
      const errorMessage = error?.error && error.error !== 'API_ERROR'
        ? error.error
        : error?.message || 'Failed to upload file';
      setError(errorMessage);
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadPlaybookLibraryTemplate();
    } catch (error: any) {
      setError(error?.message || 'Failed to download template');
    }
  };

  const startEditing = (item: PlaybookLibraryItem) => {
    setEditingItemId(item._id);
    setEditingText(item.text);
  };

  const cancelEditing = () => {
    setEditingItemId(null);
    setEditingText('');
  };

  return (
    <div className="flex flex-col h-full relative">
      {/* Deleting overlay */}
      {(deleting || uploading || (loading && items.length > 0)) && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70">
          <div className="flex flex-col items-center gap-2">
            <ArrowPathIcon className="animate-spin text-blue-600" style={{ fontSize: '2.5rem' }} />
            <span className="text-sm font-medium text-gray-600">
              {deleting ? 'Deleting...' : uploading ? 'Uploading...' : 'Refreshing...'}
            </span>
          </div>
        </div>
      )}

      {/* Description */}
      <div className="px-4 py-2 bg-blue-50 border-b border-blue-100">
        <p className="text-sm text-blue-800">
          <span className="font-medium">Library:</span> Build a reusable corpus of pitch bullets, qualifying questions, and deal progression talking points. Upload via Excel or add individually. Select from this library when creating playbooks.
        </p>
      </div>

      {/* Top bar with upload/download */}
      <div className="px-4 py-2 border-b border-gray-200 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">
            {items.length} total item{items.length !== 1 ? 's' : ''}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={fetchItems}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            title="Refresh"
            disabled={loading}
          >
            <ArrowPathIcon className={loading ? 'animate-spin' : ''} style={{ fontSize: '1.25rem' }} />
          </button>
          <button
            onClick={handleDownloadTemplate}
            className="px-3 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm"
          >
            <DownloadIcon style={{ fontSize: '1rem' }} />
            Download Template
          </button>

          <label
            className={`px-4 py-2 text-white rounded-lg flex items-center gap-2 text-sm cursor-pointer ${
              uploading ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            <UploadIcon style={{ fontSize: '1rem' }} />
            {uploading ? 'Uploading...' : 'Upload Excel'}
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleUploadExcel}
              className="hidden"
              disabled={uploading}
            />
          </label>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {loading && items.length === 0 ? (
          <div className="p-8 text-center">
            <ArrowPathIcon className="animate-spin mx-auto text-gray-400" style={{ fontSize: '2rem' }} />
            <p className="mt-2 text-gray-500">Loading library...</p>
          </div>
        ) : (
          <div className="space-y-3">
            {SECTIONS.map(({ type, label }) => {
              const sectionItems = getItemsByType(type);
              const isExpanded = expandedSections.has(type);

              return (
                <div key={type} className="border border-gray-200 rounded-lg bg-white shadow-sm">
                  {/* Section header */}
                  <div
                    className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                    onClick={() => toggleSection(type)}
                  >
                    <div className="flex items-center gap-2">
                      {isExpanded ? (
                        <ExpandLessIcon className="text-gray-400" />
                      ) : (
                        <ExpandMoreIcon className="text-gray-400" />
                      )}
                      <h3 className="font-semibold text-gray-900">
                        {label} ({sectionItems.length})
                      </h3>
                    </div>
                  </div>

                  {/* Section content */}
                  {isExpanded && (
                    <div className="border-t border-gray-200 px-4 py-3">
                      {sectionItems.length === 0 ? (
                        <p className="text-sm text-gray-400 italic mb-3">No items yet</p>
                      ) : (
                        <ul className="space-y-2 mb-3">
                          {sectionItems.map(item => (
                            <li key={item._id} className="flex items-center gap-2 group">
                              {editingItemId === item._id ? (
                                <>
                                  <input
                                    type="text"
                                    value={editingText}
                                    onChange={(e) => setEditingText(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleUpdateItem(item._id);
                                      if (e.key === 'Escape') cancelEditing();
                                    }}
                                    className="flex-1 px-3 py-1.5 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                    autoFocus
                                  />
                                  <button
                                    onClick={() => handleUpdateItem(item._id)}
                                    className="p-1 text-green-600 hover:bg-green-50 rounded"
                                    title="Save"
                                  >
                                    <CheckIcon style={{ fontSize: '1rem' }} />
                                  </button>
                                  <button
                                    onClick={cancelEditing}
                                    className="p-1 text-gray-500 hover:bg-gray-100 rounded"
                                    title="Cancel"
                                  >
                                    <CloseIcon style={{ fontSize: '1rem' }} />
                                  </button>
                                </>
                              ) : (
                                <>
                                  <span className="text-gray-400">-</span>
                                  <span className="flex-1 text-sm text-gray-700">{item.text}</span>
                                  <button
                                    onClick={() => startEditing(item)}
                                    className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                    title="Edit"
                                  >
                                    <PencilIcon style={{ fontSize: '1.125rem' }} />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteItem(item._id)}
                                    className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                    title="Delete"
                                  >
                                    <TrashIcon style={{ fontSize: '1.125rem' }} />
                                  </button>
                                </>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}

                      {/* Add item inline */}
                      {addingInSection === type ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={addingText[type]}
                            onChange={(e) => setAddingText(prev => ({ ...prev, [type]: e.target.value }))}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleAddItem(type);
                              if (e.key === 'Escape') setAddingInSection(null);
                            }}
                            placeholder={`New ${label.toLowerCase()} item...`}
                            className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            autoFocus
                          />
                          <button
                            onClick={() => handleAddItem(type)}
                            className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                          >
                            Add
                          </button>
                          <button
                            onClick={() => setAddingInSection(null)}
                            className="px-3 py-1.5 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setAddingInSection(type)}
                          className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1"
                        >
                          <PlusIcon style={{ fontSize: '0.875rem' }} />
                          Add Item
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlaybookLibraryManagement;
